import { useEffect, useState } from 'react';
import { useMessages } from '../../contexts/messages';
import ModalLg from '../ModalLg';
import { useModal } from '../../contexts/modal';
import OutboundMessageForm from '../OutboundMessageForm';
import service from '../../service';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { DateTime } from 'luxon';
import { useNavigate, useLocation } from 'react-router-dom';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { OutboundMessage } from '@Types/outboundMessage';
import { useOutboundMessage } from '../../hooks/useOutboundMessage';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { useQueryClient } from '@tanstack/react-query';
import { buildFilterParams } from '@Utils/buildFilterParams';
import ShowFor from '@Components/ShowFor';

const DEFAULT_SCHEDULED_COLUMN_CONFIG: ColumnConfig<OutboundMessage>[] = [
  {
    id: 'campaignTitle',
    headerLabel: 'Campaign',
    accessor: (row) => row.campaignTitle ?? 'N/A',
    type: 'text',
    size: 350,
  },
  {
    id: 'method',
    headerLabel: 'Channel',
    accessor: (row) => row.method ?? '',
    type: 'text',
  },
  {
    id: 'sendTime',
    headerLabel: 'Send Time',
    accessor: (row) => row.sendTime ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
];

const DEFAULT_HISTORY_COLUMN_CONFIG: ColumnConfig<OutboundMessage>[] = [
  {
    id: 'campaignTitle',
    headerLabel: 'Campaign',
    accessor: (row) => row.campaignTitle ?? 'N/A',
    type: 'text',
    size: 350,
  },
  {
    id: 'method',
    headerLabel: 'Channel',
    accessor: (row) => row.method ?? '',
    type: 'text',
  },
  {
    id: 'sentAt',
    headerLabel: 'Sent At',
    accessor: (row) => row.sentAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
  {
    id: 'numSendAttempts',
    headerLabel: 'Attempts',
    accessor: (row) => row.numSendAttempts ?? '',
    type: 'text',
  },
];

const CampaignList = () => {
  const [listType, setListType] = useState('Scheduled');
  const { fetchAllInboxes, fetchSegments } = useMessages();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { openModal, closeModal } = useModal();

  const baseParams =
    listType === 'Scheduled'
      ? {
          '[filter][sentAt]': null,
          '[filter][campaignTitle][not]': null,
        }
      : {
          '[filter][sentAt][not]': null,
          '[filter][campaignTitle][not]': null,
        };

  const columnConfigs =
    listType === 'Scheduled'
      ? DEFAULT_SCHEDULED_COLUMN_CONFIG
      : DEFAULT_HISTORY_COLUMN_CONFIG;

  const query = useQueryClient();

  const controller = useResourceTableController<OutboundMessage>({
    columnsConfig: columnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    baseSearchParams: () => ({
      ...baseParams,
    }),
    buildFilterParams: (columnFilters) => buildFilterParams(columnFilters),
    quickFilterFields: [
      { field: 'campaignTitle', label: 'Campaign' },
      { field: 'method', label: 'Communication Channel' },
    ],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'campaignTitle'],
      right: [],
    },
  });

  const { data: payload, status } = useOutboundMessage({
    searchParams: controller.searchParams,
  });

  const rows: OutboundMessage[] = Array.isArray(payload)
    ? payload
    : (payload?.data ?? []);

  const meta = Array.isArray(payload) ? undefined : payload?.meta;

  const resetTable = () => {
    query.invalidateQueries({ queryKey: ['messages'] });
  };

  const handleOpenModal = () => {
    openModal();
  };

  const handleCreateOutboundMessage = async (data) => {
    if (data.html && !data.messageBody) data.messageBody = 'html';
    if (data.sendNow) {
      // reset the sendTime to reflect 'now' (when the send button was pressed)
      data.sendTime = DateTime.local();
    }
    delete data.sendNow;

    try {
      await service.createOutboundMessage(data);
      toastSuccess(`Scheduling outbound message - successful!`);
      closeModal();
      setListType('Scheduled');
    } catch (error) {
      console.error(error);
      toastError(`Failed to schedule.`);
    }
  };

  useEffect(() => {
    fetchAllInboxes();
    fetchSegments();
  }, []);

  useEffect(() => {
    if (state) {
      setListType(state.loadTab || 'Scheduled');
    }
    navigate(location.pathname, { replace: true }); //clears state
  }, [state]);

  return (
    <div className="space-y-4">
      <div className="flex w-full justify-between">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <Button
            className={`rounded-r-none enabled:bg-gray-100 disabled:opacity-100`}
            onClick={() => {
              setListType('Scheduled');
            }}
            disabled={listType === 'Scheduled'}
          >
            Scheduled
          </Button>
          <Button
            className={`rounded-l-none enabled:bg-gray-100 disabled:opacity-100`}
            onClick={() => {
              setListType('History');
            }}
            disabled={listType === 'History'}
          >
            History
          </Button>
        </div>

        <ShowFor permissions={['CAMPAIGNS_WRITE']}>
          <Button onClick={handleOpenModal}>Create Campaign</Button>
        </ShowFor>
      </div>

      <ResourceTableView<OutboundMessage>
        label="Campaigns"
        controller={controller}
        data={rows}
        meta={meta}
        status={status as any}
        onRowClick={(row) => {
          navigate(`/campaigns/${row.id}`);
        }}
      />

      <ModalLg>
        <h2 className="mb-4 text-lg font-bold">Create Campaign</h2>
        <OutboundMessageForm
          onSubmitHandler={handleCreateOutboundMessage}
          title="Schedule Outbound Message"
          buttonText="Schedule"
          campaign={true}
          resetTable={resetTable}
        />
      </ModalLg>
    </div>
  );
};

export default CampaignList;
